import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TemporaryPersonCreated } from '@up2d8/contracts';
import { Button, Card, Input, Select } from '@up2d8/ui';
import { API_BASE, useSession } from '../../shared/session';

const DAY = 24 * 60 * 60 * 1000;
const datum = (dagen: number) => new Date(Date.now() + dagen * DAY).toISOString().slice(0, 10);

/**
 * De staat en het versturen van het formulier "tijdelijke kracht", los van hoe het eruitziet.
 *
 * Sinds BHR-2 apart: de teamleider vult het in de app in (`TemporaryForm` hieronder), HR in het
 * venster M5 van het beheer (`temporary-dialog.tsx`). Eén plek voor de controle en voor de fouten
 * van de server bij het juiste veld, twee weergaven.
 */
export function useTemporaryForm({
  groups,
  onCreated,
}: {
  groups: { id: string; name: string }[];
  onCreated?: (result: TemporaryPersonCreated) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [personType, setPersonType] = useState<'temporary' | 'contractor'>('temporary');
  const [originCompany, setOriginCompany] = useState('');
  const [endDate, setEndDate] = useState(datum(14));
  const [groupId, setGroupId] = useState(groups.length === 1 ? (groups[0]?.id ?? '') : '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TemporaryPersonCreated | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/people/temporary', {
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          personType,
          originCompany: originCompany.trim() || null,
          endDate,
          groupIds: groupId ? [groupId] : [],
        } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as TemporaryPersonCreated;
    },
    onSuccess: (data) => {
      setResult(data);
      onCreated?.(data);
    },
    onError: (error) => {
      // De fouten van de server bij het juiste veld; de rest bovenaan.
      const lijst = (error as { errors?: { path: string; message: string }[] }).errors ?? [];
      const perVeld: Record<string, string> = {};
      for (const fout of lijst) {
        perVeld[fout.path === 'plannedExitDate' ? 'endDate' : fout.path] = fout.message;
      }
      if (lijst.length === 0) {
        perVeld.form = (error as { detail?: string }).detail ?? t('temporary.failed');
      }
      setErrors(perVeld);
    },
  });

  const submit = () => {
    const fouten: Record<string, string> = {};
    if (!firstName.trim()) fouten.firstName = t('temporary.requiredError');
    if (!lastName.trim()) fouten.lastName = t('temporary.requiredError');
    if (!endDate) fouten.endDate = t('temporary.endDateError');
    setErrors(fouten);
    if (Object.keys(fouten).length === 0) create.mutate();
  };

  /** Nog iemand: de naam leeg, de rest (soort, bureau, datum, groep) blijft voor de volgende. */
  const another = () => {
    setResult(null);
    setFirstName('');
    setLastName('');
    setErrors({});
  };

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    personType,
    setPersonType,
    originCompany,
    setOriginCompany,
    endDate,
    setEndDate,
    groupId,
    setGroupId,
    errors,
    result,
    submit,
    another,
    pending: create.isPending,
    /** De vroegste einddatum: vandaag. */
    minEndDate: datum(0),
  };
}

/**
 * Een tijdelijke kracht toevoegen (Stap 31), zoals de teamleider het op zijn telefoon doet (Mijn
 * team). Het veldpatroon is dat van de zelfregistratie (REG-2, frame 3): "verplicht" of
 * "optioneel" in woorden in het label, en de fout bij het veld. Het scherm zelf is niet ontworpen
 * (`docs/design/gaps.md`). HR doet hetzelfde in het beheer, in het venster M5 (BHR-2).
 *
 * Na het aanmaken toont hetzelfde blok de code: de uitzendkracht staat ernaast en meldt zich nu
 * aan, met nummer en code of met de QR-code.
 */
export function TemporaryForm({
  groups,
  onCreated,
}: {
  /** De groepen waaruit gekozen mag worden: bij een teamleider alleen de zijne. */
  groups: { id: string; name: string }[];
  onCreated?: (result: TemporaryPersonCreated) => void;
}) {
  const { t } = useTranslation();
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    personType,
    setPersonType,
    originCompany,
    setOriginCompany,
    endDate,
    setEndDate,
    groupId,
    setGroupId,
    errors,
    result,
    submit,
    another,
    pending,
  } = useTemporaryForm({ groups, onCreated });

  if (result) {
    return <TemporaryResult result={result} onAnother={another} />;
  }

  const verplicht = t('temporary.required');
  const optioneel = t('temporary.optional');
  return (
    <div className="flex flex-col gap-4" data-testid="temporary-form">
      <Input
        label={`${t('temporary.firstName')} · ${verplicht}`}
        value={firstName}
        autoComplete="off"
        error={errors.firstName}
        onChange={(event) => setFirstName(event.target.value)}
        data-testid="temporary-first-name"
      />
      <Input
        label={`${t('temporary.lastName')} · ${verplicht}`}
        value={lastName}
        autoComplete="off"
        error={errors.lastName}
        onChange={(event) => setLastName(event.target.value)}
        data-testid="temporary-last-name"
      />
      <Select
        label={`${t('temporary.type')} · ${verplicht}`}
        value={personType}
        onChange={(event) => setPersonType(event.target.value as 'temporary' | 'contractor')}
        data-testid="temporary-type"
      >
        <option value="temporary">{t('temporary.typeTemporary')}</option>
        <option value="contractor">{t('temporary.typeContractor')}</option>
      </Select>
      <Input
        label={`${t('temporary.originCompany')} · ${optioneel}`}
        value={originCompany}
        maxLength={120}
        error={errors.originCompany}
        onChange={(event) => setOriginCompany(event.target.value)}
        data-testid="temporary-origin"
      />
      <Input
        label={`${t('temporary.endDate')} · ${verplicht}`}
        type="date"
        min={datum(0)}
        value={endDate}
        hint={t('temporary.endDateHint')}
        error={errors.endDate}
        onChange={(event) => setEndDate(event.target.value)}
        data-testid="temporary-end-date"
      />
      {groups.length > 0 ? (
        <Select
          label={`${t('temporary.group')} · ${optioneel}`}
          value={groupId}
          onChange={(event) => setGroupId(event.target.value)}
          data-testid="temporary-group"
        >
          <option value="">{t('temporary.noGroup')}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </Select>
      ) : null}
      {errors.form ? (
        <p role="alert" className="text-caption text-danger">
          {errors.form}
        </p>
      ) : null}
      <Button size="lg" loading={pending} onClick={submit} data-testid="temporary-submit">
        {t('temporary.submit')}
      </Button>
    </div>
  );
}

/** De code op het scherm, en de QR-code als hij liever scant. */
function TemporaryResult({
  result,
  onAnother,
}: {
  result: TemporaryPersonCreated;
  onAnother: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [qr, setQr] = useState(false);
  const geldig = new Date(result.activationExpiresAt).toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'long',
  });
  const einde = result.person.plannedExitDate
    ? new Date(`${result.person.plannedExitDate}T12:00:00`).toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <div className="flex flex-col gap-4" data-testid="temporary-result">
      <div>
        <p className="font-heading text-title-sm font-semibold text-text-primary">
          {result.person.firstName} {result.person.lastName}
        </p>
        <p className="text-content text-text-secondary">{t('temporary.until', { date: einde })}</p>
      </div>
      <Card>
        <dl className="flex flex-col gap-3">
          <div>
            <dt className="text-caption font-semibold uppercase tracking-wide text-text-muted">
              {t('temporary.employeeNumber')}
            </dt>
            <dd
              className="font-mono text-title-sm font-semibold text-text-primary"
              data-testid="temporary-number"
            >
              {result.employeeNumber}
            </dd>
          </div>
          <div>
            <dt className="text-caption font-semibold uppercase tracking-wide text-text-muted">
              {t('temporary.activationCode')}
            </dt>
            <dd
              className="font-mono text-title-lg font-semibold tracking-widest text-text-primary"
              data-testid="temporary-code"
            >
              {result.activationCode}
            </dd>
            <p className="mt-1 text-caption text-text-secondary">
              {t('temporary.codeValid', { date: geldig })}
            </p>
          </div>
        </dl>
      </Card>
      <p className="text-content text-text-secondary">{t('temporary.howTo')}</p>
      {result.invitationCode ? (
        qr ? (
          <Card className="flex flex-col items-center gap-2" data-testid="temporary-qr">
            <img
              src={`${API_BASE}/api/v1/public/invitations/${encodeURIComponent(result.invitationCode)}/qr`}
              alt={t('temporary.qrAlt')}
              className="h-56 w-56"
            />
            <p className="text-caption text-text-secondary">{t('temporary.qrHint')}</p>
          </Card>
        ) : (
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setQr(true)}
            data-testid="temporary-show-qr"
          >
            {t('temporary.showQr')}
          </Button>
        )
      ) : null}
      <Button variant="ghost" onClick={onAnother}>
        {t('temporary.another')}
      </Button>
    </div>
  );
}
